import { useEffect, useState } from "react";
import { Check, RefreshCw, Sparkles, Truck, PackageX, Plus, Search, Heart, Palette } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DELIVERY_STYLE = {
  free: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paid: "bg-amber-50 text-amber-700 border-amber-200",
  unavailable: "bg-charcoal/5 text-charcoal/60 border-charcoal/20",
};

const HIDDEN_LABELS = new Set(["Coming Soon", "Inspiration Only"]);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const categoryLabel = (c) => (c ? c.replace(/[-_]/g, " ").replace(/s$/, "") : "item");

// Mirrors backend _delivery_tag_for_product with the standard fee resolved.
const deliveryFor = (p, standardFee) => {
  const kind = String(p.delivery_fee_type || "free").toLowerCase();
  if (kind === "free") return { tag: "free", label: "Free Delivery", fee: 0 };
  const fee = kind === "custom" ? Math.max(0, Number(p.delivery_fee_amount || 0)) : Number(standardFee || 0);
  return { tag: "paid", label: `₹${fee} Delivery`, fee };
};

const Tag = ({ children, tone, testId }) => (
  <span data-testid={testId} className={`inline-flex flex-shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full px-1.5 py-px text-[9px] leading-[14px] font-bold uppercase tracking-wide border ${tone}`}>
    {children}
  </span>
);

const DeliveryTag = ({ delivery, testId }) => {
  if (!delivery?.label) return null;
  const Icon = delivery.tag === "unavailable" ? PackageX : Truck;
  return <Tag tone={DELIVERY_STYLE[delivery.tag] || DELIVERY_STYLE.unavailable} testId={testId}><Icon className="w-2.5 h-2.5" />{delivery.label}</Tag>;
};

export const PlanProductCard = ({ row, included, onToggle, onChange, personal }) => {
  const p = row.product;
  const idx = row.element_index;
  const aiPick = !row.user_changed;
  return (
    <div data-testid={`plan-card-${idx}`}
      className={`rounded-2xl border-2 bg-white overflow-hidden flex gap-3 p-2.5 transition-colors ${included ? "border-rose-400" : "border-warm opacity-80"}`}>
      <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 self-start rounded-xl overflow-hidden bg-linen-2 relative">
        {p.image_url
          ? <img src={p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover" data-testid={`plan-card-image-${idx}`} />
          : <Sparkles className="w-6 h-6 text-charcoal/30 absolute inset-0 m-auto" />}
        {included && (
          <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center shadow" aria-hidden="true">
            <Check className="w-3 h-3" strokeWidth={3} />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-nowrap gap-1 overflow-x-auto no-scrollbar">
          {aiPick && personal?.tag === "Must-have" && <Tag tone="bg-rose-50 text-rose-700 border-rose-300" testId={`tag-must-have-${idx}`}><Heart className="w-2.5 h-2.5" />Must-have</Tag>}
          {aiPick && personal?.tag === "Matches Their Taste" && <Tag tone="bg-amber-50 text-amber-700 border-amber-200" testId={`tag-taste-${idx}`}><Palette className="w-2.5 h-2.5" />Matches Their Taste</Tag>}
          {aiPick && !personal && <Tag tone="bg-violet-50 text-violet-700 border-violet-200" testId={`tag-ai-${idx}`}><Sparkles className="w-2.5 h-2.5" />AI Recommended</Tag>}
          {aiPick && row.match_kind === "similar" && <Tag tone="bg-sky-50 text-sky-700 border-sky-200" testId={`tag-similar-${idx}`}>Similar Alternative</Tag>}
          {aiPick && row.match_kind !== "similar" && <Tag tone="bg-emerald-50 text-emerald-700 border-emerald-200" testId={`tag-exact-${idx}`}>Exact Match</Tag>}
          {row.product_is_trending && <Tag tone="bg-rose-50 text-rose-700 border-rose-200" testId={`trending-badge-${idx}`}>Trending</Tag>}
        </div>
        <p className="text-sm font-semibold text-charcoal mt-1 leading-snug break-words" data-testid={`plan-card-name-${idx}`}>{p.name}</p>
        {aiPick && personal?.reason && (
          <p className="text-[11px] italic text-charcoal/60 leading-snug" data-testid={`plan-card-reason-${idx}`}>{personal.reason}</p>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
          <p className="text-base font-bold text-charcoal leading-tight" data-testid={`plan-card-price-${idx}`}>{inr(p.price)}</p>
          <DeliveryTag delivery={row.delivery} testId={`avail-tag-${idx}`} />
        </div>
        {row.above_budget && row.over_by > 0 && (
          <p className="text-[11px] font-semibold text-amber-700 mt-0.5" data-testid={`above-budget-${idx}`}>{inr(row.over_by)} above your budget</p>
        )}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
          {included ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700" data-testid={`plan-card-included-${idx}`}>
              <Check className="w-3 h-3" strokeWidth={3} /> Included in your plan
            </span>
          ) : (
            <button type="button" onClick={onToggle} data-testid={`plan-card-add-${idx}`}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold bg-rose-500 text-white hover:bg-rose-600 transition-colors">
              <Plus className="w-3 h-3" /> Add to plan
            </button>
          )}
          <button type="button" onClick={onChange} data-testid={`plan-card-change-${idx}`}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold border border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white transition-colors">
            <RefreshCw className="w-3 h-3" /> Change
          </button>
          {included && (
            <button type="button" onClick={onToggle} data-testid={`plan-card-remove-${idx}`}
              className="text-[11px] font-semibold text-charcoal/50 hover:text-rose-600 underline-offset-2 hover:underline">
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const ChangeProductDialog = ({ row, pincode, onPick, onClose }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [standardFee, setStandardFee] = useState(null);
  const category = row?.product?.category;

  useEffect(() => {
    api.get("/settings/public").then((r) => setStandardFee(r.data.default_delivery_fee ?? 0)).catch(() => setStandardFee(0));
  }, []);

  useEffect(() => {
    if (!row) return;
    setItems(null);
    setQ("");
    api.get("/products", { params: { category, pincode: pincode || undefined } })
      .then((r) => setItems(r.data.filter((p) => p.available !== false && !HIDDEN_LABELS.has(p.label) && Number(p.price) > 0)))
      .catch(() => setItems([]));
  }, [row, category, pincode]);

  const alt = row?.trending_alternative?.product;
  const list = (items || [])
    .filter((p) => p.id !== row?.product?.id && p.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.id === alt?.id) - (a.id === alt?.id) || a.price - b.price);

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="change-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Change this item</DialogTitle>
          <DialogDescription>Pick another {categoryLabel(category)} from our collection. Prices shown are final.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
            className="w-full rounded-xl border border-warm pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-rose-400" data-testid="change-product-search" />
        </div>
        {!items ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-xl bg-linen-2 animate-pulse" />)}</div>
        ) : list.length === 0 ? (
          <p className="text-sm text-charcoal/60 py-6 text-center" data-testid="change-product-empty">No other options available right now.</p>
        ) : (
          <div className="space-y-2">
            {list.map((p) => (
              <button key={p.id} type="button" onClick={() => onPick(p, deliveryFor(p, standardFee))} data-testid={`change-option-${p.id}`}
                className="w-full flex items-center gap-3 rounded-xl border border-warm p-2 text-left hover:border-rose-400 hover:bg-rose-50/40 transition-colors">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-linen-2 flex-shrink-0">
                  {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  {p.id === alt?.id && <p className="text-[10px] font-bold uppercase tracking-wider text-rose-600">Trending pick</p>}
                  <p className="text-sm font-semibold text-charcoal break-words">{p.name}</p>
                </div>
                <p className="font-display text-base text-charcoal whitespace-nowrap">{inr(p.price)}</p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
